import styles from './AssetHealthCardSmall.module.scss';
import { AssetHealth } from '@customer/store/apis/healthApi';
import { useTranslation } from 'react-i18next';
import { VdsCard, VdsIcon } from '@volt/volt-extended-components-react';
import AssetWidget from '@customer/components/asset-data/asset-widget/AssetWidget';
import { useCallback } from 'react';
import { useAppDispatch } from '@customer/store/hooks';
import {
  AssetSidePanelTab,
  FaultLevel,
  openSidePanel,
  SidePanel,
} from '@customer/store/slices/globalSlice';
import { SystemTypePpu } from '@customer/store/apis/assetsApi';
import AssetIdentifier from '@customer/components/asset-data/asset-identifier/AssetIdentifier';
import { useLocaleFormatter } from '@customer/shared/hooks/useLocaleFormatter';

export interface AssetHealthCardSmallProps {
  asset: AssetHealth;
  faultLevel: FaultLevel;
}

const levelColours = [
  'var(--static-red-500)',
  'var(--static-orange-500)',
  'var(--static-yellow-500)',
];

export function AssetHealthCardSmall({ asset, faultLevel }: AssetHealthCardSmallProps) {
  const { t } = useTranslation();
  const { getNumberPercentText } = useLocaleFormatter();

  const dispatch = useAppDispatch();

  const onClicked = useCallback(() => {
    dispatch(
      openSidePanel({
        sidePanel: SidePanel.Asset,
        modelId: asset.id.toString(),
        // Open the side panel asset config tab if dealing with PPU
        tab:
          asset.hw === SystemTypePpu
            ? AssetSidePanelTab.Configuration
            : AssetSidePanelTab.SelfTestSummary,
        // Only open the fault level accordion for non-PPU
        defaultAccordion: asset.hw === SystemTypePpu ? undefined : faultLevel,
      }),
    );
  }, [dispatch, asset.id, asset.hw, faultLevel]);

  const faultLevelIndex = Number(faultLevel) - 1;

  const color = levelColours[faultLevelIndex];
  const numActiveFaults = asset.fa[faultLevelIndex];
  const percentage = asset.levelPercentages[faultLevelIndex];

  return (
    <VdsCard className={styles.card} padding="spacing-00" button onClick={onClicked}>
      <div className={styles.container}>
        <header className={styles.header}>
          <AssetIdentifier
            name={asset.displayName}
            mismatch={!asset.stsNam}
            deviceType={asset.displayHw}
            isCompact
          />
          <VdsIcon svgIcon="open-icon" size="20px" color="color-icon-medium" />
        </header>
        <div className={styles.content}>
          <AssetWidget title={t('TimeInLevelFault', { level: faultLevel })} isCompact width="100%">
            <div>{getNumberPercentText(percentage)}</div>
            <div
              className={styles.percentbar}
              style={{ width: `${percentage * 100}%`, background: `${color}` }}
            />
          </AssetWidget>
          <AssetWidget title={t('ActiveTotalFaults')} isCompact width="100%">
            <span className={styles.faults}>
              <span
                className={`${styles.activeFaults} ${numActiveFaults === 0 && styles['activeFaults--none']}`}
              >
                {numActiveFaults}
              </span>
              <span className={styles.separator}> / </span>
              {asset.fc[faultLevelIndex]}
            </span>
          </AssetWidget>
        </div>
      </div>
    </VdsCard>
  );
}

export default AssetHealthCardSmall;
